import type { VertexProvenance } from "../../wasm/editOps.ts";
import { vertexCentroid } from "../editSelection.ts";
import { computeFaceNormal } from "../faceNormal.ts";
import { identityProvenance } from "../provenance.ts";
import {
  identityRemap,
  rebuildTopologyEdgesPreservingWire,
  triangulateTopologyForRendering,
} from "../topology.ts";
import type { PolygonTopology, RenderTriangleIndices } from "../types.ts";

interface FlipResult {
  vertices: Float32Array;
  indices: RenderTriangleIndices;
  topology: PolygonTopology;
  provenance: VertexProvenance;
}

/**
 * Flip normals for selected vertices (or all if none selected).
 * Reverses winding order of affected polygon faces.
 */
export function flipNormals(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
): FlipResult {
  const { offsets, corners } = topology;
  const faceCount = offsets.length - 1;
  const allSelected = selected.size === 0;

  // Determine which faces to flip: any face with at least one selected vertex
  const facesToFlip = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    if (allSelected) {
      facesToFlip.add(f);
    } else {
      for (let j = 0; j < size; j++) {
        if (selected.has(corners[start + j])) {
          facesToFlip.add(f);
          break;
        }
      }
    }
  }

  // Reverse winding in topology corners for affected faces
  const newCorners = new Uint32Array(corners);
  for (const f of facesToFlip) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < Math.floor(size / 2); j++) {
      const a = start + j;
      const b = start + size - 1 - j;
      const tmp = newCorners[a];
      newCorners[a] = newCorners[b];
      newCorners[b] = tmp;
    }
  }

  const newTopology = rebuildTopologyEdgesPreservingWire(
    new Uint32Array(offsets),
    newCorners,
    topology,
    identityRemap,
  );

  const newIndices = triangulateTopologyForRendering(newTopology);
  // Vertex layout is unchanged — only winding flipped.
  return {
    vertices,
    indices: newIndices,
    topology: newTopology,
    provenance: identityProvenance(vertices.length / 3),
  };
}

/**
 * Recalculate normals to point outward (consistent winding).
 * Uses the mesh centroid as the "inside" reference — each face normal
 * should point away from the centroid.
 */
export function recalculateOutside(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
): FlipResult {
  const { offsets, corners } = topology;
  const faceCount = offsets.length - 1;
  const allSelected = selected.size === 0;

  // Compute centroid of affected vertices
  const affectedVerts = new Set<number>();
  const facesToCheck = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    let affected = allSelected;
    if (!affected) {
      for (let j = 0; j < size; j++) {
        if (selected.has(corners[start + j])) {
          affected = true;
          break;
        }
      }
    }
    if (affected) {
      facesToCheck.add(f);
      for (let j = 0; j < size; j++) affectedVerts.add(corners[start + j]);
    }
  }

  const [cx, cy, cz] = vertexCentroid(vertices, affectedVerts) ?? [0, 0, 0];

  // For each affected face, compute its geometric normal and face center.
  // If the normal points toward the centroid, reverse the winding.
  const newCorners = new Uint32Array(corners);
  // View topology over the in-progress newCorners so the shared face-normal
  // helper sees this op's mutations. Each face owns a disjoint corner slice,
  // so reversing one face never disturbs another's normal calculation.
  const workingTopology: PolygonTopology = { ...topology, corners: newCorners };
  const normalScratch: [number, number, number] = [0, 0, 0];
  for (const f of facesToCheck) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    if (size < 3) continue;

    const normal = computeFaceNormal(
      vertices,
      workingTopology,
      f,
      normalScratch,
    );
    if (!normal) continue;

    const [fcx, fcy, fcz] = vertexCentroid(
      vertices,
      newCorners.subarray(start, start + size),
    ) ?? [0, 0, 0];

    // If normal points inward (toward centroid), reverse winding.
    if (
      normal[0] * (fcx - cx) + normal[1] * (fcy - cy) + normal[2] * (fcz - cz) <
      0
    ) {
      for (let j = 0; j < Math.floor(size / 2); j++) {
        const a = start + j;
        const b = start + size - 1 - j;
        const tmp = newCorners[a];
        newCorners[a] = newCorners[b];
        newCorners[b] = tmp;
      }
    }
  }

  const newTopology = rebuildTopologyEdgesPreservingWire(
    new Uint32Array(offsets),
    newCorners,
    topology,
    identityRemap,
  );

  const newIndices = triangulateTopologyForRendering(newTopology);

  // Vertex layout is unchanged — only winding flipped on selected faces.
  return {
    vertices,
    indices: newIndices,
    topology: newTopology,
    provenance: identityProvenance(vertices.length / 3),
  };
}
